/**
 * Notifiche sul telefono (web push) — DEC-04 rivista 2026-09-25.
 * Su iPhone funzionano solo con l'app aggiunta alla schermata Home (iOS 16.4+): `stato()` lo dice.
 * Il permesso si chiede solo dopo un tocco di lei (regola di iOS e buona educazione).
 */
import { oggiApi } from './api/oggi'

export type StatoNotifiche =
  | 'non_supportate'
  | 'serve_home' // iPhone aperto nel browser: prima "Aggiungi alla schermata Home"
  | 'da_attivare'
  | 'negate'
  | 'attive'

function isIos(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
}

function isStandalone(): boolean {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  )
}

export async function registraServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null
  try {
    return await navigator.serviceWorker.register('/sw.js', { scope: '/' })
  } catch {
    return null
  }
}

export async function stato(): Promise<StatoNotifiche> {
  if (isIos() && !isStandalone()) return 'serve_home'
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
    return 'non_supportate'
  }
  if (Notification.permission === 'denied') return 'negate'
  if (Notification.permission !== 'granted') return 'da_attivare'
  const reg = await navigator.serviceWorker.getRegistration('/')
  const sub = await reg?.pushManager.getSubscription()
  return sub ? 'attive' : 'da_attivare'
}

function chiaveServer(base64url: string): ArrayBuffer {
  const padding = '='.repeat((4 - (base64url.length % 4)) % 4)
  const raw = atob((base64url + padding).replace(/-/g, '+').replace(/_/g, '/'))
  const out = new Uint8Array(new ArrayBuffer(raw.length))
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i)
  return out.buffer
}

/** Chiamata dal tocco su "Attiva le notifiche". Ritorna lo stato finale. */
export async function attiva(): Promise<StatoNotifiche> {
  const config = await oggiApi.pushConfig()
  if (!config.enabled || !config.public_key) return 'non_supportate'
  const permesso = await Notification.requestPermission()
  if (permesso !== 'granted') return permesso === 'denied' ? 'negate' : 'da_attivare'
  const reg = (await registraServiceWorker()) ?? (await navigator.serviceWorker.ready)
  const sub =
    (await reg.pushManager.getSubscription()) ??
    (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: chiaveServer(config.public_key) }))
  const json = sub.toJSON() as { endpoint: string; keys: { p256dh: string; auth: string } }
  await oggiApi.subscribe({ endpoint: json.endpoint, keys: json.keys })
  return 'attive'
}

/** Numero sull'icona = promemoria di oggi ancora da fare. 0 lo toglie. */
export function aggiornaBadge(daFare: number): void {
  const nav = navigator as Navigator & { setAppBadge?: (n?: number) => Promise<void>; clearAppBadge?: () => Promise<void> }
  if (daFare > 0) void nav.setAppBadge?.(daFare).catch(() => undefined)
  else void nav.clearAppBadge?.().catch(() => undefined)
}
